import { ABTestManager } from "./ABTestManager";
import { MainTracker } from "../PixelTrackers/MainTracker";
import type { OtcPopupClickType, OtcPopupProduct, OtcPopupProductSource, OtcPopupVariant } from "data-types";

export const OTC_POPUP_TEST_NAME = "otc_popup";
export const OTC_POPUP_VARIANTS: readonly OtcPopupVariant[] = ["A", "B", "C"];
export const OTC_POPUP_TRACKING_PROP = "OTC_PopUp_Test";
export const OTC_POPUP_CONTINUE_EVENT = "Funnel Click Continue To Shipping";
export const OTC_POPUP_DISPLAY_EVENT = "Funnel OTC Popup Display";
export const OTC_POPUP_CLICK_EVENT = "Funnel OTC Popup Click";

export const OTC_POPUP_ELIGIBLE_COUNTRIES: readonly string[] = ["de"];

const STORAGE_KEY = `abtest_${OTC_POPUP_TEST_NAME}`;
let assignedVariant: OtcPopupVariant | null = null;
let continueToShippingCategory: string | null = null;

export interface OtcPopupTrackingContext {
  mixpanel_category: string | null;
  selected_product_catalog: number | null;
  selected_product_item: number | null;
}

const isVariant = (value: unknown): value is OtcPopupVariant =>
  typeof value === "string" && (OTC_POPUP_VARIANTS as readonly string[]).includes(value);

export const parseOtcPopupWeights = (raw: unknown): number[] => {
  const control = [1, 0, 0];
  if (raw === null || raw === undefined || raw === "") return control;
  let parsed: unknown = raw;
  if (typeof raw === "string") {
    try {
      parsed = JSON.parse(raw);
    } catch {
      return control;
    }
  }
  if (!parsed || typeof parsed !== "object") return control;
  const rawWeights = OTC_POPUP_VARIANTS.map((arm) => (parsed as Record<string, unknown>)[arm]);
  if (rawWeights.some((w) => typeof w !== "number" || !Number.isFinite(w) || w < 0)) return control;
  const weights = rawWeights as number[];
  if (weights[1] === 0 && weights[2] === 0) return control;
  const total = weights.reduce((sum, w) => sum + w, 0);
  if (total <= 0) return control;
  return weights.map((w) => w / total);
};

export const isOtcPopupExperimentEligible = (ctx: { country?: string | null; isCarePlus?: boolean | null }): boolean =>
  OTC_POPUP_ELIGIBLE_COUNTRIES.includes((ctx.country ?? "").toLowerCase()) && !ctx.isCarePlus;

export const assignOtcPopupVariant = (
  rawWeights: unknown,
  ctx: { country?: string | null; isCarePlus?: boolean | null },
): OtcPopupVariant | null => {
  assignedVariant = null;
  if (!isOtcPopupExperimentEligible(ctx) || rawWeights === null || rawWeights === undefined || rawWeights === "") return null;
  const weights = parseOtcPopupWeights(rawWeights);
  if (weights[1] === 0 && weights[2] === 0) {
    if (typeof window !== "undefined") {
      try {
        localStorage.removeItem(STORAGE_KEY);
      } catch {
      }
    }
    return "A";
  }
  const manager = new ABTestManager({
    testName: OTC_POPUP_TEST_NAME,
    variants: [...OTC_POPUP_VARIANTS],
    weights,
  });
  const raw = manager.getVariant();
  assignedVariant = isVariant(raw) ? raw : "A";
  return assignedVariant;
};

export const getOtcPopupTrackingProps = (): Partial<Record<typeof OTC_POPUP_TRACKING_PROP, OtcPopupVariant>> => {
  return assignedVariant ? { [OTC_POPUP_TRACKING_PROP]: assignedVariant } : {};
};

export const setContinueToShippingCategory = (mixpanelCategory: string | null): void => {
  continueToShippingCategory = mixpanelCategory;
};

export const trackContinueToShipping = (): void => {
  MainTracker.track("custom", OTC_POPUP_CONTINUE_EVENT, { ...getOtcPopupTrackingProps(), mixpanel_category: continueToShippingCategory });
};

export const trackOtcPopupDisplay = (ctx: OtcPopupTrackingContext): void => {
  MainTracker.track("custom", OTC_POPUP_DISPLAY_EVENT, { ...getOtcPopupTrackingProps(), ...ctx });
};

export const buildOtcPopupClickProps = (
  ctx: OtcPopupTrackingContext,
  click: {
    clickType: OtcPopupClickType;
    product?: Pick<OtcPopupProduct, "catalog_id" | "id" | "source"> | null;
    position?: number;
    source?: OtcPopupProductSource;
    searchResultCount?: number;
  },
): Record<string, unknown> => ({
  ...getOtcPopupTrackingProps(),
  ...ctx,
  click_type: click.clickType,
  ...(click.product
    ? { otc_product_catalog: click.product.catalog_id, otc_product_item: click.product.id, product_source: click.source ?? click.product.source }
    : {}),
  ...(click.position !== undefined ? { product_position: click.position } : {}),
  ...(click.searchResultCount !== undefined ? { search_result_count: click.searchResultCount } : {}),
});

export const trackOtcPopupClick = (ctx: OtcPopupTrackingContext, click: Parameters<typeof buildOtcPopupClickProps>[1]): void => {
  MainTracker.track("custom", OTC_POPUP_CLICK_EVENT, buildOtcPopupClickProps(ctx, click));
};
